import { Box, Card, Divider, Snackbar, Alert, Typography, Tab } from "@mui/material";
import styles from './PreOrder.module.scss';
import { TabContext, TabList, TabPanel } from "@mui/lab";
import TitleSubtitle from "../../components/common/formControls/titleSubtitle/TitleSubtitle";
import { useTranslation } from "react-i18next";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { generateClearPreOrder, generateClearPreOrderValidationErrors } from "../../common/EntitiesManager";
import { PreOrderData, PreOrderValidationError, ToastNotification } from "../../common/Interfaces";
import InputTextFieldMultiline from "../../components/common/formControls/textField/InputTextFieldMultiline";
import ClientData from "../../components/clientData/ClientData";
import ProductData from "../../components/productData/ProductData";
import CancelButton from "../../components/common/formControls/buttons/CancelButton";
import SuccessButton from "../../components/common/formControls/buttons/SuccessButton";
import { useAuthentication } from "../../common/requests/LoginRequest";
import { getAlertType, getDateInYYYYMMDDHHmmss } from "../../common/Utils";
import { ToastType } from "../../common/requests/Enums";
import { addObservation, createPreOrder, getPreOrder } from "../../common/requests/PreOrdersRequests";
import EventStatusData from "../../components/eventStatusData/EventStatusData";
import AppLoading from "../../components/common/loading/AppLoading";
import StatusChangeTimeLine from "../statusChangeTimeLine/StatusChangeTimeLine";
import BasicModal from "../../components/common/basicModal/BasicModal";
import { createEmailNotification } from "../../common/requests/EmailNotificationRequests";

export default function PreOrder (){
    const auth = useAuthentication();    
    const {t} = useTranslation();
    const {id} = useParams();
    const navigate = useNavigate();
    const [preOrder, setPreOrder] = useState<PreOrderData>(generateClearPreOrder());
    const [preOrderValidations, setPreOrderValidations] = useState<PreOrderValidationError>(generateClearPreOrderValidationErrors());
    const [toastInfo, setToastInfo] = useState<ToastNotification|undefined>(undefined);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [tabValue, setTabValue] = useState<string>("1");
    const [openConfirmationModal, setOpenConfirmationModal] = useState<boolean>(false);
    const [openAddObservationModal, setOpenAddObservationModal] = useState<boolean>(false);
    const [newObservation, setNewObservation] = useState<string>('');
    const maximumCharacters = 10000;

    const getPreOrderById = async (preOrderId: number) => {
        try{
            setIsLoading(true);
            const preOrderResult = await getPreOrder(preOrderId, auth.token ?? '', auth.sessionData.ManagementId);
            setPreOrder(preOrderResult ?? generateClearPreOrder());
        }finally{
            setIsLoading(false);
        }
    }

    useEffect(() => {
        try{
            if(id && id.length > 0){
                getPreOrderById(Number.parseInt(id));
            }
        }
        catch (error: any){
            console.error(error.message);
        }
    }, [id]);

    const handleObservationChange = (e: any) => {
        setPreOrder({
            ...preOrder,
            Observation: e.target.value
        });
    }

    const handleEmailNotification = async (preOrderToNotify: any, preOrderCode?: string) => {
        try{
            const creationResult = await createEmailNotification(preOrderToNotify, preOrderCode ?? '',
                auth.token ?? '', 
                auth.sessionData.ManagementId, 
                auth.sessionData.CompanyId,
                auth.sessionData.UserName);
            
            console.log(JSON.stringify(creationResult));
        }   
        catch(error: any){
            console.error(error.message);
        }
    }

    const handleCreatePreOrder = async () => {
        if(!!validatePreOrder()){
            setIsProcessing(true);
            setToastInfo({
                type: ToastType.info,
                message: t("CREATINGPREORDER"),
            });

            const creationResult = await createPreOrder(preOrder, 
                                                        auth.token ?? '', 
                                                        auth.sessionData.ManagementId, 
                                                        auth.sessionData.CompanyId,
                                                        auth.sessionData.UserName);
            if (!!creationResult.Ok){
                handleEmailNotification(preOrder, creationResult.data);
                setToastInfo({
                    type: ToastType.info,
                    message: t("PREORDERCREATED"),
                });

                setIsProcessing(false);
                navigate("/");
                return;
            }

            setToastInfo({
                type: ToastType.error,
                message: creationResult.data ? `${t("UNEXPECTEDERROR")}: ${JSON.stringify(creationResult.data)}` : t("UNEXPECTEDERROR"),
            })

            setIsProcessing(false);
            return;
        }

        setToastInfo({
            type: ToastType.error,
            message: t("THEREAREFIELDWITHERRORS"),
        });
    }

    const validatePreOrder = ():boolean =>{
        if(!preOrder.ClientIdentificationType){
            setPreOrderValidations({
                ...generateClearPreOrderValidationErrors(),
                ClientIdentificationType: t("EMPTYFIELDERROR"),
            });
            return false;
        }

        if(!preOrder.ClientIdentification || preOrder.ClientIdentification.trim().length === 0){
            setPreOrderValidations({
                ...generateClearPreOrderValidationErrors(),
                ClientIdentification: t("EMPTYFIELDERROR"),
            });
            return false;
        }

        if(!preOrder.ClientName || preOrder.ClientName.trim().length === 0){
            setPreOrderValidations({
                ...generateClearPreOrderValidationErrors(),
                ClientName: t("EMPTYFIELDERROR"),
            });
            return false;
        }

        if(!preOrder.State || preOrder.State.Id === "0"){
            setPreOrderValidations({
                ...generateClearPreOrderValidationErrors(),
                ClientState: t("EMPTYFIELDERROR"),
            });
            return false;
        }

        if(!preOrder.City || preOrder.City.Id === "0"){
            setPreOrderValidations({
                ...generateClearPreOrderValidationErrors(),
                ClientCity: t("EMPTYFIELDERROR"),
            });
            return false;
        }

        if(!preOrder.ClientAddress || preOrder.ClientAddress.trim().length === 0){
            setPreOrderValidations({
                ...generateClearPreOrderValidationErrors(),
                ClientAddress: t("EMPTYFIELDERROR"),
            });
            return false;
        }

        if(!preOrder.ClientPhoneNumber || preOrder.ClientPhoneNumber.trim().length === 0){
            setPreOrderValidations({
                ...generateClearPreOrderValidationErrors(),
                ClientPhoneNumber: t("EMPTYFIELDERROR"),
            });
            return false;
        }

        if(!preOrder.ProductType || preOrder.ProductType.Id === "0"){
            setPreOrderValidations({
                ...generateClearPreOrderValidationErrors(),
                ProductType: t("EMPTYFIELDERROR"),
            });
            return false;
        }

        if(!preOrder.ProductBrand || preOrder.ProductBrand.Id === "0"){
            setPreOrderValidations({
                ...generateClearPreOrderValidationErrors(),
                ProductBrand: t("EMPTYFIELDERROR"),
            });
            return false;
        }

        if(!preOrder.Observation || preOrder.Observation.trim().length === 0){
            setPreOrderValidations({
                ...generateClearPreOrderValidationErrors(),
                Observation: t("EMPTYFIELDERROR"),
            });
            return false;
        }

        if (!preOrder.ProductLocation || preOrder.ProductLocation.trim().length === 0){
            setPreOrderValidations({
                ...generateClearPreOrderValidationErrors(),
                ProductLocation: t("EMPTYFIELDERROR"),
            });
            return false;
        }

        setPreOrderValidations(generateClearPreOrderValidationErrors());
        return true;
    }

    const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
        setTabValue(newValue);
    }

    const handleAddObservation = async () => {
        if(!newObservation || newObservation.length === 0 || newObservation.replaceAll(" ", "").length === 0){
            setToastInfo({
                type: ToastType.error,
                message: t("MUSTENTERANOBSERVATION"),
            });
            return;
        }

        setIsProcessing(true);
       
        setToastInfo({
            type: ToastType.info,
            message: `${t("ADDINGOBSERVATION")}...`,
        });

        const modificationResult = await addObservation(preOrder.Id, newObservation, getDateInYYYYMMDDHHmmss(new Date()), auth.token ?? '', auth.sessionData.ManagementId);

        if (!!modificationResult.Ok){
            setToastInfo({
                type: ToastType.info,
                message: t("OBSERVATIONMODIFIED"),
            });

            setIsProcessing(false);
            window.location.reload();
            return;
        }

        setToastInfo({
            type: ToastType.error,
            message: modificationResult.data ? `${t("UNEXPECTEDERROR")}: ${JSON.stringify(modificationResult.data)}` : t("UNEXPECTEDERROR"),
        })

        setIsProcessing(false);
        return;
    }

    const handleNewObservationChange = (e: any) => {
        setNewObservation(e.target.value);
    }

    return auth.isAuthenticated ? (
    <Box className={styles.container}>
        {isLoading ? 
            (<AppLoading/>):
            (
                <Box className={styles.internalContainerColumn}>
                    <BasicModal 
                        openModal={!!openAddObservationModal}
                        setOpenModal={setOpenAddObservationModal}
                        title={`${t("ADD")} ${t("OBSERVATIONS")}`}
                        subTitle={t("ADDOBSERVATIONWARNIGN")}
                        successButton={{
                            id:"addObservationBtn",
                            label:t("SAVE"),
                            onClick: () => {
                                setOpenAddObservationModal(false);
                                handleAddObservation();
                            },
                            isDisabled:!newObservation || newObservation.length === 0
                        }}
                        cancelButton={{
                            id:"cancelObservationBtn",
                            label:t("CANCEL"),
                            onClick: () => {setOpenAddObservationModal(false)}
                        }}
                        component={<InputTextFieldMultiline
                            id={"AddObservation"} 
                            maximumCharacters={maximumCharacters - preOrder.Observation.length}
                            label={""}
                            value={newObservation}
                            handleValueChange={handleNewObservationChange}
                            />}
                    />

                    <BasicModal
                        openModal={!!openConfirmationModal}
                        setOpenModal={setOpenConfirmationModal}
                        title={t("CONFIRMSAVE")}
                        subTitle={t("QUESTIONWANTTOSAVETHEPREORDER")}
                        successButton={{
                            id: 'confirmationButton',
                            label: t("YES"),
                            onClick: () => {
                                setOpenConfirmationModal(false);
                                handleCreatePreOrder();
                            }
                        }}
                        cancelButton={{
                            id: 'CancelButton',
                            label: t("CANCEL"),
                            onClick: () => {setOpenConfirmationModal(false)}
                        }}
                    />

                    <Snackbar
                        sx={{minWidth: "400px", maxWidth: "100%"}}
                        open={!!toastInfo}
                        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                        autoHideDuration={3000}
                        onClose={() => {
                            setToastInfo(undefined);
                        }}
                    >
                        <Alert severity={getAlertType(toastInfo?.type ?? ToastType.info)}>
                            <Typography>
                                {toastInfo?.message}
                            </Typography>
                        </Alert>
                    </Snackbar>

                    <Box className={styles.titleContainer}>
                        <TitleSubtitle title={`${id ? `${t("DETAIL")} ${t("OF")}` : t("NEWFEM")} ${t("PREORDER")}: ${preOrder.Code}`}
                                subTitle={`${t(!id ? "NEWPREORDERDESCRIPTION" : "DETAILPREORDERDESCRIPTION")}`}/>
                        {preOrder.Id > 0 && (
                            <EventStatusData preOrder={preOrder}/>
                        )}
                    </Box>
                    
                    <TabContext value={tabValue}>
                        <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                            <TabList onChange={handleTabChange} aria-label="preorder tabs" >
                                <Tab label={t("GENERALDATA")} value="1"/>
                                {(preOrder.CurrentStatus || preOrder.Event && preOrder.Event.Id > 0) && (<Tab label={t("STATUSCHANGES")} value="2"/>)}
                            </TabList>
                        </Box>
                        <TabPanel value="1">
                            <Box className={styles.internalContainer}>
                                <ClientData preOrder={preOrder} setPreOrder={setPreOrder} preOrderValidations={preOrderValidations} auth={auth}/>
                                <ProductData preOrder={preOrder} setPreOrderData={setPreOrder} preOrderValidations={preOrderValidations} auth={auth} />

                                <Card>
                                    <Box className={styles.verticalContainer}>
                                        <TitleSubtitle title={t("REQUESTDETAIL")}/>
                                        <Divider/>
                                        <InputTextFieldMultiline id={"Observation"} 
                                                                maximumCharacters={maximumCharacters} 
                                                                label={""} 
                                                                errorMessage={preOrderValidations.Observation}
                                                                value={preOrder.Observation}
                                                                isReadOnly={preOrder.Id > 0}
                                                                handleValueChange={handleObservationChange}/>
                                    </Box>
                                </Card>
                            </Box>
                        </TabPanel>
                        {preOrder && (
                            <TabPanel value = "2">
                                <StatusChangeTimeLine preOrder={preOrder} session={auth} />
                            </TabPanel>
                        )}
                    </TabContext>

                    <Box className={styles.footerButtonsContainer}>
                        {(!id || id.length === 0) && (
                            <SuccessButton id={"CreatePreorderButton"} label={`${t("CREATE")} ${t("PREORDER")}`} onclickHandler={() => {setOpenConfirmationModal(true)}} disabled={isProcessing}/>
                        )}

                        {preOrder.Id > 0 && !preOrder.CurrentStatus && (!preOrder.Event || preOrder.Event?.CanEdit === 1) && (
                            <SuccessButton id={"AddObservation"} label={`${t("ADD")} ${t("OBSERVATIONS")}`} onclickHandler={() => {
                                            setNewObservation(''); 
                                            setOpenAddObservationModal(true)}
                                        }
                                        disabled={isProcessing}/>
                        )}
                        
                        <Link to="/">
                            <CancelButton id={"CancelPreorderButton"} label={t((!id || id.length === 0) ? "CANCEL" : "BACK")} onclickHandler={undefined} disabled={isProcessing} />
                        </Link>
                    </Box>
                </Box>
            )}
    </Box>
    ):
    (<div/>);
}